import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io'

import { useTranslation } from 'i18n-web/i18next'

const Component = styled.nav`
  margin-top: 40px;
  margin-bottom: 10px;
  font-size: ${props => props.theme.font.size.mobile.small};

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    font-size: ${props => props.theme.font.size.desktop.large};
  }

  .iconWrapper {
    display: flex;
    align-items: center;
  }

  .icon {
    fill: ${props => props.theme.palette.grayDark};
  }
`

const List = styled.ul`
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 1;
`

const Button = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  font-weight: bold;
  line-height: 1;
  color: ${props => props.theme.palette.gray};
  background: none;
  border: none;

  &:visited {
    color: ${props => props.theme.palette.gray};
  }

  &.highlight {
    color: ${props => props.theme.palette.primary};
  }
`

const FirstPageButton = styled(Button)`
  padding-right: 40px;

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    padding-right: 50px;
  }
`

const LastPageButton = styled(Button)`
  padding-left: 40px;
  color: ${props => props.theme.palette.gray};

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    padding-left: 50px;
  }
`

const CurrentPageButton = styled(Button)`
  padding: 0 40px;
  color: ${props => props.theme.palette.primary};

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    padding: 0 50px;
  }
`

const ArrowButton = styled(Button)`
  padding: 0;
  font-size: ${props => props.theme.font.size.desktop.large};
`

const Pagination = ({
  currentPage,
  moreResultsAvailable,
  onClick,
  pageCount,
}) => {
  const { t } = useTranslation()

  const PrevIcon = () => (
    <span className="iconWrapper" aria-hidden="true">
      <IoIosArrowBack className="icon" />
    </span>
  )

  const NextIcon = () => (
    <span className="iconWrapper" aria-hidden="true">
      <IoIosArrowForward className="icon" />
    </span>
  )

  const firstPage = 1
  const previousPage = currentPage - 1
  const nextPage = currentPage + 1
  const lastPage = pageCount

  const getPagination = () => {
    if (currentPage == 1) {
      return (
        <>
          <li>
            <FirstPageButton
              aria-label={`${t('common.pagination.first')} (${firstPage})`}
              className="highlight">
              {currentPage}
            </FirstPageButton>
          </li>
          <li>
            <ArrowButton
              aria-label={`${t('common.pagination.next')} (${nextPage})`}
              onClick={e => onClick(e, nextPage)}>
              <NextIcon />
            </ArrowButton>
          </li>
          <li>
            <LastPageButton
              aria-label={`${t('common.pagination.last')} (${lastPage})`}
              onClick={e => onClick(e, lastPage)}>
              {lastPage}
            </LastPageButton>
          </li>
        </>
      )
    }

    if (currentPage != 1 && moreResultsAvailable) {
      return (
        <>
          <li>
            <FirstPageButton
              aria-label={`${t('common.pagination.first')} (${firstPage})`}
              onClick={e => onClick(e, firstPage)}>
              {firstPage}
            </FirstPageButton>
          </li>
          <li>
            <ArrowButton
              aria-label={`${t(
                'common.pagination.previous'
              )} (${previousPage})`}
              onClick={e => onClick(e, previousPage)}>
              <PrevIcon />
            </ArrowButton>
          </li>
          <CurrentPageButton>{currentPage}</CurrentPageButton>
          <li>
            <ArrowButton
              aria-label={`${t('common.pagination.next')} (${nextPage})`}
              onClick={e => onClick(e, nextPage)}>
              <NextIcon />
            </ArrowButton>
          </li>
          <li>
            <LastPageButton
              aria-label={`${t('common.pagination.last')} (${lastPage})`}
              onClick={e => onClick(e, lastPage)}>
              {lastPage}
            </LastPageButton>
          </li>
        </>
      )
    }

    return (
      <>
        <li className="pageItem">
          <FirstPageButton
            aria-label={`${t('common.pagination.first')} (${firstPage})`}
            onClick={e => onClick(e, firstPage)}>
            {firstPage}
          </FirstPageButton>
        </li>
        <li>
          <ArrowButton
            aria-label={`${t('common.pagination.previous')} (${previousPage})`}
            onClick={e => onClick(e, previousPage)}>
            <PrevIcon />
          </ArrowButton>
        </li>
        <li>
          <LastPageButton
            aria-label={`${t('common.pagination.last')} (${lastPage})`}
            className="highlight">
            {currentPage}
          </LastPageButton>
        </li>
      </>
    )
  }

  return (
    <Component aria-label={t('common.pagination.title')}>
      <List>{getPagination()}</List>
    </Component>
  )
}

Pagination.propTypes = {
  currentPage: PropTypes.number,
  moreResultsAvailable: PropTypes.bool,
  onClick: PropTypes.func,
  pageCount: PropTypes.number,
}

export default Pagination
